import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { APIError } from '@localagent/sdk';
import type {
  ContentIdentityAnchor,
  WorkspaceEnvironmentDeclaration,
  WorkspaceProjection,
} from '@localagent/sdk';
import { getSDK, useDeviceId } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { bridgeEventStreamAtom, sdkConfiguredAtom } from '../stores/sdk/atoms';
import { useShogunchop } from '../providers/ShogunChopProvider';
import {
  launchWorkspaceAction,
  dismissWorkspaceAction,
  launchedWorkspaceIdsAtom,
} from '../stores/workspace/atoms';
import {
  applyWorkspaceProjectionAction,
  workspaceProjectionAtomFamily,
} from '../stores/workspace/projection';

export function useWorkspaces() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const deviceId = useDeviceId();

  return useQuery({
    queryKey: ['workspaces', deviceId],
    queryFn: async () => {
      // Ask the SDK, per attempt. It owns the web-vs-daemon decision, and on
      // desktop the daemon's id moves during sign-in — so a value captured
      // before that (including this hook's own `deviceId`, which only keys the
      // cache) would pin the pre-adopt id. A retry re-resolves and recovers.
      const sdk = getSDK();
      return sdk.workspaces.list(await sdk.deviceId());
    },
    enabled: isAuthenticated && sdkConfigured,
    retry: (failureCount, err) => isDeviceSettling(err) && failureCount < 8,
    retryDelay: (failureCount) => Math.min(250 * 2 ** failureCount, 2_000),
  });
}

/**
 * A device-scoped read that failed only because the device is not registered
 * YET — a startup race, not a failure.
 *
 * On desktop the daemon owns registration and performs it while acquiring a
 * session: it mints a local `device_id` at boot, and only on the auth callback
 * does it register and then adopt the backend-canonical id. Measured on a
 * post-`la desktop clean` start, that window is ~10s. A read inside it gets
 * 404 "Device not found", and rendering that as a terminal error tells the user
 * their workspaces failed to load when they are simply not reachable yet.
 *
 * This is a bounded retry of a transient, not a fallback: nothing is
 * substituted, and an error that outlives the window still surfaces
 * (`.claude/rules/no-fallbacks.md`).
 */
export function isDeviceSettling(err: unknown): boolean {
  return (
    err instanceof APIError &&
    (err.status === 404 || err.status === 403) &&
    /device/i.test(err.message)
  );
}

export function useWorkspaceProjection(workspaceId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const deviceId = useDeviceId();
  const bridgeStream = useAtomValue(bridgeEventStreamAtom);
  const shogunchop = useShogunchop();
  const state = useAtomValue(workspaceProjectionAtomFamily(workspaceId ?? ''));
  const applyProjection = useSetAtom(applyWorkspaceProjectionAction);
  const canFetch = Boolean(isAuthenticated && sdkConfigured && workspaceId);
  const refreshedProjectionTarget = useRef<string | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<Error | null>(null);

  const query = useQuery({
    queryKey: ['workspace-projection', workspaceId, deviceId],
    queryFn: async () => getSDK().workspaces.getState(workspaceId!, (await getSDK().deviceId()) ?? undefined),
    enabled: canFetch,
  });
  const { data, isFetching, refetch } = query;
  const projectionLoaded = data !== undefined;

  useEffect(() => {
    if (!data) return;
    applyProjection({ type: 'projection_refetched', projection: data });
  }, [applyProjection, data]);

  useEffect(() => {
    if (!state.refreshRequested) {
      refreshedProjectionTarget.current = null;
      return;
    }
    const target = state.revisionGap
      ? `workspace:${state.revisionGap.to}`
      : `index:${state.refreshIndexRevision}`;
    if (!canFetch || refreshedProjectionTarget.current === target || isFetching) return;
    refreshedProjectionTarget.current = target;
    void refetch();
  }, [
    canFetch,
    isFetching,
    refetch,
    state.refreshIndexRevision,
    state.refreshRequested,
    state.revisionGap,
  ]);

  useEffect(() => {
    if (!canFetch || !workspaceId || !deviceId || !projectionLoaded) return;

    let cancelled = false;
    const sdk = getSDK();
    const receive = (projection: WorkspaceProjection, source: 'snapshot' | 'event') => {
      if (!cancelled) {
        applyProjection({
          type: source === 'snapshot' ? 'projection_refetched' : 'projection_received',
          projection,
        });
      }
    };
    const reportRegistrationError = (error: unknown) => {
      const registrationError = error instanceof Error ? error : new Error(String(error));
      console.error('[workspace-state] event subscription failed', registrationError);
      if (!cancelled) setSubscriptionError(registrationError);
    };
    setSubscriptionError(null);

    const daemon = shogunchop.resource;
    if (daemon) {
      const relay = daemon.sharedEvents();
      const registration = relay.registerWorkspaceProjection(
        workspaceId,
        () => sdk.workspaces.getState(workspaceId, deviceId),
        (afterRevision) =>
          sdk.workspaces.subscribeEvents(workspaceId, {
            device_id: deviceId,
            after_revision: afterRevision,
          }),
        () => sdk.workspaces.unsubscribeEvents(workspaceId, deviceId),
        receive,
        reportRegistrationError
      );
      registration.registered
        .then(() => {
          if (!cancelled) setSubscriptionError(null);
        })
        .catch(reportRegistrationError);

      return () => {
        cancelled = true;
        registration.unsubscribe();
        relay.release();
      };
    }

    if (!bridgeStream) return;
    const registration = bridgeStream.registerWorkspaceProjection(workspaceId, receive);
    registration.registered.catch(reportRegistrationError);

    return () => {
      cancelled = true;
      registration.unsubscribe();
    };
  }, [
    applyProjection,
    bridgeStream,
    canFetch,
    deviceId,
    projectionLoaded,
    refetch,
    shogunchop.resource,
    workspaceId,
  ]);

  return {
    ...query,
    deviceId,
    error: query.error ?? subscriptionError,
    projection: state.projection,
    refreshRequested: state.refreshRequested,
    revisionGap: state.revisionGap,
  };
}

export function useWorkspaceOperationHistory(workspaceId: string | null, revision: number) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery({
    queryKey: ['workspace-operation-history', workspaceId, revision],
    queryFn: () => getSDK().workspaces.getOperationHistory(workspaceId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && workspaceId),
  });
}

function useWorkspaceProjectionMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<WorkspaceProjection>
) {
  const applyProjection = useSetAtom(applyWorkspaceProjectionAction);

  return useMutation({
    mutationFn,
    onSuccess: (projection) => {
      applyProjection({ type: 'projection_received', projection });
    },
  });
}

export function useBindWorkspace(workspaceId: string) {
  return useWorkspaceProjectionMutation(
    (params: { deviceId: string; folderPath: string; idempotencyKey: string }) =>
      getSDK().workspaces.bind(workspaceId, {
        device_id: params.deviceId,
        folder_path: params.folderPath,
        idempotency_key: params.idempotencyKey,
      })
  );
}

/**
 * Move a local workspace onto THIS computer. The device id is asked of the SDK
 * per attempt, never captured — the outgoing device may be long gone
 * (workspace_state ADR-028).
 */
export function useReassignWorkstation(workspaceId: string) {
  return useWorkspaceProjectionMutation(
    async (params: { folderPath: string; idempotencyKey: string }) => {
      const device_id = await getSDK().deviceId();
      return getSDK().workspaces.reassignWorkstation(workspaceId, {
        device_id,
        folder_path: params.folderPath,
        idempotency_key: params.idempotencyKey,
      });
    }
  );
}

export function useUnbindWorkspace(workspaceId: string) {
  return useWorkspaceProjectionMutation((params: { deviceId: string; idempotencyKey: string }) =>
    getSDK().workspaces.unbind(workspaceId, params.deviceId, {
      idempotency_key: params.idempotencyKey,
    })
  );
}

export function useTransferWorkspace(workspaceId: string) {
  return useWorkspaceProjectionMutation(
    (params: {
      deviceId: string;
      direction: 'push' | 'pull';
      expectedSource?: ContentIdentityAnchor;
      expectedDestination?: ContentIdentityAnchor;
      resolveDivergence?: boolean;
      idempotencyKey: string;
    }) =>
      getSDK().workspaces.transfer(workspaceId, {
        device_id: params.deviceId,
        direction: params.direction,
        expected_source: params.expectedSource,
        expected_destination: params.expectedDestination,
        resolve_divergence: params.resolveDivergence,
        idempotency_key: params.idempotencyKey,
      })
  );
}

export function useCancelWorkspaceOperation(workspaceId: string) {
  return useWorkspaceProjectionMutation((params: { operationId: string; idempotencyKey: string }) =>
    getSDK().workspaces.cancelOperation(workspaceId, params.operationId, {
      idempotency_key: params.idempotencyKey,
    })
  );
}

export function useRetryWorkspaceArchiveCleanup(workspaceId: string) {
  return useWorkspaceProjectionMutation((operationId: string) =>
    getSDK().workspaces.retryArchiveCleanup(workspaceId, { operation_id: operationId })
  );
}

export function useSaveWorkspaceEnvironment(workspaceId: string) {
  return useWorkspaceProjectionMutation(
    (params: { declaration: WorkspaceEnvironmentDeclaration; idempotencyKey: string }) =>
      getSDK().workspaces.saveEnvironment(workspaceId, {
        declaration: params.declaration,
        idempotency_key: params.idempotencyKey,
      })
  );
}

export function useConvertWorkspace(workspaceId: string) {
  return useWorkspaceProjectionMutation((idempotencyKey: string) =>
    getSDK().workspaces.convert(workspaceId, { idempotency_key: idempotencyKey })
  );
}

export function useWakeWorkspace(workspaceId: string) {
  return useWorkspaceProjectionMutation((idempotencyKey: string) =>
    getSDK().workspaces.wake(workspaceId, { idempotency_key: idempotencyKey })
  );
}

export function useSleepWorkspace(workspaceId: string) {
  return useWorkspaceProjectionMutation((idempotencyKey: string) =>
    getSDK().workspaces.sleep(workspaceId, { idempotency_key: idempotencyKey })
  );
}

export function useRegisterWorkspace() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const launchWorkspace = useSetAtom(launchWorkspaceAction);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: { workspaceRoot: string; name?: string; idempotencyKey: string }) => {
      // Asked of the SDK per attempt — the daemon's id moves during sign-in.
      const device_id = await getSDK().deviceId();
      return getSDK().workspaces.createLocal({
        workspace_root: args.workspaceRoot,
        name: args.name,
        device_id,
        idempotency_key: args.idempotencyKey,
      });
    },
    onSuccess: (projection) => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        launchWorkspace(projection.workspace_id);
      }
    },
  });
}

export function useCreateCloudWorkspace() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();
  const applyProjection = useSetAtom(applyWorkspaceProjectionAction);

  return useMutation({
    mutationFn: async (args: { name?: string; idempotencyKey: string; sourceRoot?: string }) => {
      if (args.sourceRoot) {
        const source_device_id = await getSDK().deviceId();
        return getSDK().workspaces.create({
          kind: 'cloud',
          name: args.name,
          source_device_id,
          source_root: args.sourceRoot,
          idempotency_key: args.idempotencyKey,
        });
      }
      return getSDK().workspaces.create({
        kind: 'cloud',
        name: args.name,
        idempotency_key: args.idempotencyKey,
      });
    },
    onSuccess: (projection) => {
      if (!isAuthenticated) return;
      applyProjection({ type: 'projection_received', projection });
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

export function useArchiveWorkspace() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const applyProjection = useSetAtom(applyWorkspaceProjectionAction);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      idempotencyKey,
    }: {
      workspaceId: string;
      idempotencyKey: string;
    }) => getSDK().workspaces.archive(workspaceId, { idempotency_key: idempotencyKey }),
    onSuccess: (projection) => {
      if (isAuthenticated) {
        applyProjection({ type: 'projection_received', projection });
        queryClient.setQueriesData<WorkspaceProjection[]>({ queryKey: ['workspaces'] }, (current) =>
          current?.map((workspace) =>
            workspace.workspace_id === projection.workspace_id ? projection : workspace
          )
        );
      }
    },
    onError: (_error, variables) => {
      if (!isAuthenticated) return;
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      void queryClient.invalidateQueries({
        queryKey: ['workspace-projection', variables.workspaceId],
      });
    },
  });
}

/**
 * Drop launched ids whose workspace no longer exists.
 *
 * The launched list is persisted, so a workspace deleted elsewhere leaves an id
 * behind and every consumer re-requests it forever (a 404 per render). Runs ONCE
 * over the already-fetched list rather than per id: reconciling inside the per-id
 * query turns each dismissal into a re-render that re-drives the query.
 */
export function useReconcileLaunchedWorkspaces(): void {
  const { data: workspaces, isSuccess } = useWorkspaces();
  const launchedIds = useAtomValue(launchedWorkspaceIdsAtom);
  const dismissWorkspace = useSetAtom(dismissWorkspaceAction);

  useEffect(() => {
    // Only a SUCCESSFUL list proves absence — a failed or pending fetch must
    // never un-launch a workspace that is merely unreachable.
    if (!isSuccess || !workspaces) return;
    const live = new Set(
      workspaces
        .filter((workspace) => workspace.lifecycle !== 'archived')
        .map((workspace) => workspace.workspace_id)
    );
    for (const id of launchedIds) {
      if (!live.has(id)) dismissWorkspace(id);
    }
  }, [isSuccess, workspaces, launchedIds, dismissWorkspace]);
}
